import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus, Phone, Mail } from "lucide-react";
import { trimPhoneNumber, validatePhoneNumberWithCommas } from "@/utils/phoneNumberFormatter";

interface ContactInfoEntry {
  id: string; // For internal management only
  type: 'phone' | 'email';
  phone?: string;
  email?: string;
  is_primary: boolean;
  type_category: 'official' | 'personal' | 'cell' | 'store' | 'others';
}

interface DynamicContactInfoFieldProps {
  initialPhone?: Array<{phone: string; type: string; is_primary: boolean}> | string;
  initialEmail?: Array<{email: string; type: string; is_primary: boolean}> | string;
  onContactInfoChange: (data: { 
    phone?: Array<{phone: string; type: "official" | "personal" | "cell" | "store" | "others"; is_primary: boolean}>; 
    email?: Array<{email: string; type: "official" | "personal" | "cell" | "store" | "others"; is_primary: boolean}>;
    contactInfo?: ContactInfoEntry[] 
  }) => void;
}

const CONTACT_TYPES = [
  { value: "phone", label: "Phone", icon: Phone },
  { value: "email", label: "Email", icon: Mail },
];

const CONTACT_CATEGORIES = [
  { value: "official", label: "Official" },
  { value: "personal", label: "Personal" },
  { value: "cell", label: "Cell" },
  { value: "store", label: "Store" },
  { value: "others", label: "Others" },
];

const DynamicContactInfoField = ({ 
  initialPhone, 
  initialEmail, 
  onContactInfoChange 
}: DynamicContactInfoFieldProps) => {
  const isInitialized = useRef(false);
  const entryCounter = useRef(0);
  
  const generateStableId = (type: 'phone' | 'email') => {
    entryCounter.current += 1;
    return `${type}-stable-${entryCounter.current}`;
  };
  
  // Initialize state from props only once
  const [contactEntries, setContactEntries] = useState<ContactInfoEntry[]>(() => {
    const entries: ContactInfoEntry[] = [];
    
    // Handle initialPhone (can be JSON array or string)
    if (initialPhone) {
      if (Array.isArray(initialPhone)) {
        initialPhone.forEach((phoneItem) => {
          entries.push({
            id: generateStableId('phone'),
            type: "phone",
            phone: phoneItem.phone,
            is_primary: phoneItem.is_primary,
            type_category: phoneItem.type as 'official' | 'personal' | 'cell' | 'store' | 'others',
          });
        });
      } else if (typeof initialPhone === 'string' && initialPhone.trim()) {
        entries.push({
          id: generateStableId('phone'),
          type: "phone",
          phone: initialPhone,
          is_primary: true,
          type_category: "official",
        });
      }
    }
    
    // Handle initialEmail (can be JSON array or string)
    if (initialEmail) {
      if (Array.isArray(initialEmail)) {
        initialEmail.forEach((emailItem) => {
          entries.push({
            id: generateStableId('email'),
            type: "email",
            email: emailItem.email,
            is_primary: emailItem.is_primary,
            type_category: emailItem.type as 'official' | 'personal' | 'cell' | 'store' | 'others',
          });
        });
      } else if (typeof initialEmail === 'string' && initialEmail.trim()) {
        entries.push({
          id: generateStableId('email'),
          type: "email",
          email: initialEmail,
          is_primary: true,
          type_category: "official",
        });
      }
    }
    
    // If no initial data, add one phone and one email entry
    if (entries.length === 0) {
      entries.push(
        {
          id: generateStableId('phone'),
          type: "phone",
          phone: "",
          is_primary: true,
          type_category: "official",
        },
        {
          id: generateStableId('email'),
          type: "email",
          email: "",
          is_primary: true,
          type_category: "official",
        }
      );
    }
    
    isInitialized.current = true;
    return entries;
  });

  const updateFormValues = useCallback((entries: ContactInfoEntry[]) => {
    console.log("🔄 DynamicContactInfoField: updateFormValues called with entries:", entries);
    
    const phoneEntries = entries.filter(e => e.type === 'phone' && e.phone?.trim());
    const emailEntries = entries.filter(e => e.type === 'email' && e.email?.trim());
    
    // Generate JSON arrays for phone and email
    const phoneArray = phoneEntries
      .filter(entry => entry.phone?.trim())
      .map(entry => ({
        phone: entry.phone!,
        type: entry.type_category as "official" | "personal" | "cell" | "store" | "others",
        is_primary: entry.is_primary
      }));
    
    const emailArray = emailEntries
      .filter(entry => entry.email?.trim())
      .map(entry => ({
        email: entry.email!,
        type: entry.type_category as "official" | "personal" | "cell" | "store" | "others",
        is_primary: entry.is_primary
      }));
    
    console.log("📞 DynamicContactInfoField: Final phoneArray:", phoneArray);
    console.log("📧 DynamicContactInfoField: Final emailArray:", emailArray);
    
    // Always send arrays (empty if no entries) instead of undefined
    // This ensures the form properly updates when all entries are deleted
    onContactInfoChange({
      phone: phoneArray, // Always send array, even if empty
      email: emailArray, // Always send array, even if empty
      contactInfo: entries,
    });
  }, [onContactInfoChange]);

  const addContactEntry = useCallback((type: 'phone' | 'email') => {
    const newEntry: ContactInfoEntry = {
      id: generateStableId(type),
      type,
      [type]: "", // Sets phone: "" or email: ""
      is_primary: false,
      type_category: "official",
    };
    setContactEntries(prev => {
      const updated = [...prev, newEntry];
      updateFormValues(updated);
      return updated;
    });
  }, [updateFormValues]);

  const removeContactEntry = useCallback((id: string) => {
    setContactEntries(prev => {
      const updatedEntries = prev.filter(entry => entry.id !== id);
      
      // If we removed a primary entry, make the first entry of that type primary
      const removedEntry = prev.find(e => e.id === id);
      if (removedEntry?.is_primary) {
        const sameTypeEntries = updatedEntries.filter(e => e.type === removedEntry.type);
        if (sameTypeEntries.length > 0) {
          sameTypeEntries[0].is_primary = true;
        }
      }
      
      updateFormValues(updatedEntries);
      return updatedEntries;
    });
  }, [updateFormValues]);

  const updateContactEntry = useCallback((id: string, field: keyof ContactInfoEntry, value: string | boolean) => {
    setContactEntries(prev => {
      const updatedEntries = prev.map(entry => {
        if (entry.id === id) {
          const updated = { ...entry, [field]: value };
          
          // If setting this entry as primary, unset all other primary entries of the same type
          if (field === 'is_primary' && value === true) {
            prev.forEach(otherEntry => {
              if (otherEntry.type === entry.type && otherEntry.id !== id) {
                otherEntry.is_primary = false;
              }
            });
          }
          
          return updated;
        }
        return entry;
      });
      
      updateFormValues(updatedEntries);
      return updatedEntries;
    });
  }, [updateFormValues]);

  const getIcon = (type: 'phone' | 'email') => {
    const contactType = CONTACT_TYPES.find(t => t.value === type);
    const IconComponent = contactType?.icon || Phone;
    return <IconComponent className="h-4 w-4" />;
  };

  const validateEntry = (entry: ContactInfoEntry): string | null => {
    const value = entry.type === 'phone' ? entry.phone : entry.email;
    if (!value?.trim()) return null;
    
    if (entry.type === 'phone') {
      const validation = validatePhoneNumberWithCommas(value);
      return validation.isValid ? null : validation.error || "Invalid phone number";
    }
    
    if (entry.type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value) ? null : "Invalid email address";
    }
    
    return null;
  };

  const phoneEntries = contactEntries.filter(e => e.type === 'phone');
  const emailEntries = contactEntries.filter(e => e.type === 'email');

  return (
    <div className="space-y-4">
      <Label className="text-sm font-medium">Contact Information</Label>

      {/* Phone Numbers Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            <span className="text-sm font-medium">Phone Numbers</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addContactEntry('phone')}
            className="flex items-center gap-2 h-8 w-8 p-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {phoneEntries.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <Phone className="h-6 w-6 mx-auto mb-1 opacity-50" />
            <p className="text-sm">No phone numbers added</p>
          </div>
        )}

        {phoneEntries.map((entry) => {
          const error = validateEntry(entry);
          return (
            <div key={entry.id} className="flex gap-2 items-center p-3 border rounded-lg bg-card">
              <div className="flex-shrink-0">
                {getIcon(entry.type)}
              </div>
              <Select
                value={entry.type_category}
                onValueChange={(value) => updateContactEntry(entry.id, 'type_category', value)}
              >
                <SelectTrigger className="w-28 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_CATEGORIES.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="+1 234 567-8890"
                value={entry.phone || ""}
                onChange={(e) => updateContactEntry(entry.id, 'phone', e.target.value)}
                className={`h-9 flex-1 ${error ? 'border-destructive' : ''}`}
              />
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={entry.is_primary}
                  onCheckedChange={(checked) => updateContactEntry(entry.id, 'is_primary', checked)}
                />
                <span className="text-sm text-muted-foreground">Primary</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeContactEntry(entry.id)}
                className="flex-shrink-0 h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>

      {/* Email Addresses Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span className="text-sm font-medium">Email Addresses</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addContactEntry('email')}
            className="flex items-center gap-2 h-8 w-8 p-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {emailEntries.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <Mail className="h-6 w-6 mx-auto mb-1 opacity-50" />
            <p className="text-sm">No email addresses added</p>
          </div>
        )}

        {emailEntries.map((entry) => {
          const error = validateEntry(entry);
          return (
            <div key={entry.id} className="flex gap-2 items-center p-3 border rounded-lg bg-card">
              <div className="flex-shrink-0">
                {getIcon(entry.type)}
              </div>
              <Select
                value={entry.type_category}
                onValueChange={(value) => updateContactEntry(entry.id, 'type_category', value)}
              >
                <SelectTrigger className="w-28 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_CATEGORIES.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="email"
                placeholder="email@company.com"
                value={entry.email || ""}
                onChange={(e) => updateContactEntry(entry.id, 'email', e.target.value)}
                className={`h-9 flex-1 ${error ? 'border-destructive' : ''}`}
              />
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={entry.is_primary}
                  onCheckedChange={(checked) => updateContactEntry(entry.id, 'is_primary', checked)}
                />
                <span className="text-sm text-muted-foreground">Primary</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeContactEntry(entry.id)}
                className="flex-shrink-0 h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DynamicContactInfoField;